import { Injectable } from '@angular/core';
import { GetSourceDetailsRequest, Source, SourceApiService } from '@vendasta/reputation';
import { Observable, combineLatest, map, shareReplay, switchMap, firstValueFrom } from 'rxjs';
import { partnerId } from '../../globals';
import { LocationsService, isBrand } from '../locations';
import { Channel, ReviewRequestApiService } from '@vendasta/reputation';

@Injectable({ providedIn: 'root' })
export class ReputationService {
  partnerSourceDetailsMap$: Observable<Map<string, Source>>;
  public brandSources$: Observable<number[]>;
  public brandSourcesFull$: Observable<Map<string, Source>>;

  constructor(
    readonly sourceApiService: SourceApiService,
    private locationsService: LocationsService,
    private reviewRequestApiService: ReviewRequestApiService,
  ) {
    this.partnerSourceDetailsMap$ = this.locationsService.marketId$.pipe(
      switchMap((marketId) => {
        return sourceApiService.getSourceDetails(
          new GetSourceDetailsRequest({
            partnerId: partnerId,
            marketId: marketId,
          }),
        );
      }),
      map((res) => res.sources),
      map((sources) => {
        const sourceMap = new Map<string, Source>();
        sources.forEach((source) => {
          sourceMap.set(source.sourceId + '', source);
        });
        return sourceMap;
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.brandSources$ = combineLatest([this.locationsService.currentLocation$, this.partnerSourceDetailsMap$]).pipe(
      map(([brand, sourceDetailsMap]) => {
        if (!isBrand(brand)) {
          // TODO: There is a list of sources for an account that we can use instead of the partner's here,
          // but we may need to get it directly from reputation management...
          return Array.from(sourceDetailsMap.keys()).map((k) => parseInt(k, 10));
        }

        // This filter exists to filter out sources that are not available on the partner but that are enabled
        // on the brand. This can be sources that were disabled on the PID after the brand was created, sources
        // that are no longer available for whatever reason, or sources that were erroneously enabled on a
        // brand in some other way.
        return brand.activatedSources.filter((srcId) => sourceDetailsMap.has(srcId + ''));
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.brandSourcesFull$ = combineLatest([this.partnerSourceDetailsMap$, this.brandSources$]).pipe(
      map(([sourceDetailsMap, activatedSources]: [Map<string, Source>, number[]]) => {
        return activatedSources.reduce((srcs, srcId) => {
          const src = sourceDetailsMap.get(srcId + '');
          if (src) {
            srcs.set(srcId + '', src);
          }
          return srcs;
        }, new Map<string, Source>());
      }),
    );
  }

  async scheduleReviewRequest(accountGroupId: string, contactIds: string[]) {
    const req = {
      accountGroupId: accountGroupId,
      contactIds: contactIds,
      channel: Channel.SMS_FALLBACK_TO_EMAIL,
      sendNow: true,
    };
    await firstValueFrom(this.reviewRequestApiService.scheduleReviewRequest(req));
  }
}
